import { logNetworkRequest } from 'Diagnostics/diagnostics';
import { CustomErrorType } from 'Models/index';
import type { VehicleExtra } from 'Models/vehicle';
import { isObjectRecord } from 'Utils/typeGuards';

import { asCmError, GET, GETBACKUP, POST, POSTBACKUP } from './common/httpClient';

export interface CustomerGarageResponse {
  Garage: VehicleExtra[];
  SelectedVehicle?: VehicleExtra;
}

export async function saveCustomerGarage(
  customerId: string,
  garage: VehicleExtra[],
  selectedVehicle: VehicleExtra,
): Promise<void> {
  try {
    await POST('vehicles/customer.json', { customerId }, JSON.stringify({ garage, selectedVehicle }), {
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
    });
  } catch (primaryErr) {
    const primaryError = asCmError(primaryErr);
    if (primaryError.type !== CustomErrorType.failedToFetch) {
      console.warn(primaryErr || "Failed to save the customer's garage");
      // general network error with no status code can be handled here
    }

    try {
      await POSTBACKUP('garage.json', { customerId }, JSON.stringify({ garage, selectedVehicle }), {
        headers: { 'Content-Type': 'application/json' },
        keepalive: true,
      });
      if (primaryError.type !== CustomErrorType.requestAborted) {
        logNetworkRequest({
          endpoint: 'vehicles/customer.json',
          scenario: 'backup_used',
          primaryError,
        });
      }
    } catch (backupErr) {
      const backupError = asCmError(backupErr);
      console.warn(backupErr || "Failed to save the customer's garage");
      if (primaryError.type !== CustomErrorType.requestAborted) {
        logNetworkRequest({
          endpoint: 'vehicles/customer.json',
          scenario: 'backup_failed',
          primaryError,
          backupError,
        });
      }
    }
  }
}

export async function getCustomerGarage(customerId: string): Promise<CustomerGarageResponse | undefined> {
  try {
    return getCustomerGarageResponse(
      await GET(
        'vehicles/customer.json',
        { customerId },
        {
          headers: { 'Content-Type': 'application/json' },
          keepalive: true,
        },
      ),
    );
  } catch (primaryErr) {
    const primaryError = asCmError(primaryErr);
    if (primaryError.type !== CustomErrorType.failedToFetch) {
      console.warn(primaryErr || "Failed to load the customer's garage");
      // general network error with no status code can be handled here
    }

    try {
      const backupResponse = getCustomerGarageResponse(
        await GETBACKUP(
          'vehicles/customer.json',
          { customerId },
          {
            headers: { 'Content-Type': 'application/json' },
            keepalive: true,
          },
        ),
      );
      if (primaryError.type !== CustomErrorType.requestAborted) {
        logNetworkRequest({
          endpoint: 'vehicles/customer.json',
          scenario: 'backup_used',
          primaryError,
        });
      }
      return backupResponse;
    } catch (backupErr) {
      const backupError = asCmError(backupErr);
      console.warn(backupErr || "Failed to load the customer's garage");
      if (primaryError.type !== CustomErrorType.requestAborted) {
        logNetworkRequest({
          endpoint: 'vehicles/customer.json',
          scenario: 'backup_failed',
          primaryError,
          backupError,
        });
      }
    }
  }
}

function getCustomerGarageResponse(value: unknown): CustomerGarageResponse | undefined {
  return isCustomerGarageResponse(value) ? value : undefined;
}

export function isCustomerGarageResponse(value: unknown): value is CustomerGarageResponse {
  if (!isObjectRecord(value) || !Array.isArray(value.Garage) || !value.Garage.every(isVehicleExtra)) {
    return false;
  }

  return value.SelectedVehicle === undefined || isVehicleExtra(value.SelectedVehicle);
}

export function isVehicleExtra(value: unknown): value is VehicleExtra {
  return isObjectRecord(value) && Object.values(value).every((fieldValue) => typeof fieldValue === 'string');
}
